import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
// Defer loading of socket.io-client until we actually need a connection
import type { Socket } from 'socket.io-client';
import { SocketEvent, SocketEventHandler } from '../socket/unifiedSocket';
import { HIDDEN_RESYNC_MS, RECONNECT_RESYNC_GRACE_MS, requestResync } from '../socket/resync';
import { AppContext } from '../window';

const ENDPOINT = "/";

export interface SocketContextType {
  socket: Socket | null;
  subscribeToEvent: <T = any>(channel: string, handler: SocketEventHandler<T>) => () => void;
  unsubscribeFromEvent: <T = any>(channel: string, handler: SocketEventHandler<T>) => void;
}

export const SocketContext = createContext<SocketContextType>({
  socket: null,
  subscribeToEvent: () => () => {},
  unsubscribeFromEvent: () => {}
});

export const useSocketContext = () => useContext(SocketContext);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const appContext = useContext(AppContext);
  
  // Store subscriptions in a ref to avoid re-renders
  const subscriptions = useRef<Map<string, Set<SocketEventHandler<any>>>>(new Map());

  // Subscribe to event
  const subscribeToEvent = useCallback(function<T = any>(channel: string, handler: SocketEventHandler<T>) {
    if (!subscriptions.current.has(channel)) {
      subscriptions.current.set(channel, new Set());
    }
    subscriptions.current.get(channel)!.add(handler as SocketEventHandler<any>);

    // Return unsubscribe function
    return () => {
      unsubscribeFromEvent(channel, handler);
    };
  }, []);

  // Unsubscribe from event
  const unsubscribeFromEvent = useCallback(function<T = any>(channel: string, handler: SocketEventHandler<T>) {
    const channelSubs = subscriptions.current.get(channel);
    if (channelSubs) {
      channelSubs.delete(handler as SocketEventHandler<any>);
      if (channelSubs.size === 0) {
        subscriptions.current.delete(channel);
      }
    }
  }, []);

  useEffect(() => {
    let isCancelled = false;
    let socketInstance: Socket | null = null;
    let hiddenAt: number | null = null;

    let hasConnected = false;
    let pendingResync: ReturnType<typeof setTimeout> | null = null;
    const cancelPendingResync = () => {
      if (pendingResync !== null) {
        clearTimeout(pendingResync);
        pendingResync = null;
      }
    };

    // Back from a gap: with the socket connected resync now; a disconnected one
    // reconnects (unless the Manager is already retrying) and its `connect`
    // resyncs, so one gap gives one resync. A socket still down after
    // RECONNECT_RESYNC_GRACE_MS does not hold the HTTP re-read back any longer.
    const resumeAfterGap = (reason: 'visible' | 'online') => {
      if (socketInstance && !socketInstance.connected) {
        socketInstance.connect();
        if (pendingResync === null) {
          pendingResync = setTimeout(() => {
            pendingResync = null;
            requestResync(reason);
          }, RECONNECT_RESYNC_GRACE_MS);
        }
        return;
      }
      requestResync(reason);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (hiddenAt !== null) {
        const hiddenFor = Date.now() - hiddenAt;
        hiddenAt = null;
        if (hiddenFor >= HIDDEN_RESYNC_MS) {
          resumeAfterGap('visible');
        }
      }
    };
    const onOnline = () => resumeAfterGap('online');

    async function init() {
      // Only initialize socket connection for authenticated users
      if (!appContext.user?.id || !appContext.user?.credentials?.apiToken) {
        return;
      }
      try {
        const { io } = await import(/* webpackChunkName: "socket.io-client" */ 'socket.io-client');
        if (isCancelled) return;

        socketInstance = io(ENDPOINT, {
          query: { 
            id: appContext.user.id,
            siteId: appContext.siteId, 
            token: appContext.user.credentials.apiToken 
          },
          transports: ['websocket', 'polling'],
          upgrade: true,
          rememberUpgrade: true,
          timeout: 20000,
          // Never give up: a phone loses the connection on the lock screen, in a
          // lift or between wifi and 4G for longer than any fixed number of tries
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 10000,
          reconnectionAttempts: Infinity,
          randomizationFactor: 0.5
        });

        if (isCancelled) {
          socketInstance.disconnect();
          return;
        }
        setSocket(socketInstance);

        socketInstance.on("event", (event: SocketEvent) => {
          const channelSubs = subscriptions.current.get(event.channel);
          if (channelSubs) {
            channelSubs.forEach(handler => handler(event));
          }
        });

        // Every connect after the first ends a gap (the Manager's reconnect,
        // or connect() after `online` / a visible tab)
        socketInstance.on('connect', () => {
          console.debug('Socket connected');
          cancelPendingResync();
          if (hasConnected) {
            requestResync('reconnect');
          }
          hasConnected = true;
        });
  
        socketInstance.on('disconnect', (reason) => {
          console.debug('Socket disconnected:', reason);
          if (reason === 'io server disconnect') {
            socketInstance && socketInstance.connect();
          }
        });
  
        socketInstance.on('connect_error', (error) => {
          console.warn('Socket connection error:', error);
        });
  
        // Reconnection events fire on the Manager, not on the Socket
        socketInstance.io.on('reconnect', (attemptNumber) => {
          console.debug('Socket reconnected after', attemptNumber, 'attempts');
        });
  
        socketInstance.io.on('reconnect_failed', () => {
          console.warn('Socket failed to reconnect after maximum attempts');
        });

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('online', onOnline);
      } catch (e) {
        console.warn('Failed to load socket.io-client dynamically', e);
      }
    }

    init();

    return () => {
      isCancelled = true;
      cancelPendingResync();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
      if (socketInstance) {
        socketInstance.disconnect();
      }
    };
  }, [appContext.user?.id, appContext.siteId, appContext.user?.credentials?.apiToken]);

  const contextValue = useMemo<SocketContextType>(() => ({
    socket,
    subscribeToEvent,
    unsubscribeFromEvent,
  }), [socket, subscribeToEvent, unsubscribeFromEvent]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

// Custom hook for components to easily subscribe to socket events
export function useSocketEvent<T = any>(channel: string, handler: SocketEventHandler<T>) {
  const { subscribeToEvent } = useSocketContext();
  
  useEffect(() => {
    const unsubscribe = subscribeToEvent<T>(channel, handler);
    return unsubscribe;
  }, [channel, handler, subscribeToEvent]);
}